// Chat Interface Component
// Following existing patterns from ProfileEditor

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ChatInterfaceProps } from '../../../types/messaging';
import { useRealtimeMessaging } from '../../../hooks';
import { messagingService } from '../../../services/Messaging/messagingService';
import { MessageComposer } from '../MessageComposer';
import { PremiumModal } from '../../Premium';
import { MessageItem } from '../MessageItem';
import { TypingIndicator } from '../TypingIndicator';
import { ConversationSettings } from '../ConversationSettings';
import {
  ChatContainer,
  ChatHeader,
  ChatHeaderInfo,
  ChatHeaderAvatar,
  ChatHeaderDetails,
  ChatHeaderName,
  ChatHeaderStatus,
  ChatHeaderActions,
  ChatActionButton,
  MessageList,
  ChatLoading,
  ChatError,
  EmptyChatState,
  EmptyChatIcon,
  EmptyChatText
} from '../../../styles/Messaging';
import { logger } from '../../../utils/logger';
import { useSubscriptionContext } from '../../../providers/SubscriptionProvider';
import { canReplyInConversation } from '../../../utils/messagingAccess';

const NewMessagesPill = styled.button`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 96px;
  z-index: 20;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--border-medium);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);

  &:hover {
    background: var(--bg-tertiary);
  }
`;

const ReplyLockPanel = styled.div`
  margin: 0.75rem;
  padding: 1rem;
  border-radius: 14px;
  border: 1px solid rgba(255, 140, 66, 0.35);
  background: rgba(255, 140, 66, 0.08);
  text-align: center;
`;

const ReplyLockTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.35rem;
`;

const ReplyLockText = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 0.75rem;
`;

const ReplyLockButton = styled.button`
  background: linear-gradient(135deg, #ff6b9d, #ff8c42);
  border: none;
  border-radius: 999px;
  color: white;
  cursor: pointer;
  font-weight: 600;
  padding: 0.6rem 1.4rem;
`;

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  conversation,
  currentUserId,
  onSendMessage,
  onMessageAction,
  onConversationChanged,
  conversations = [] // Add conversations prop for forwarding
}) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const { isPremium } = useSubscriptionContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  // Whether the reader has scrolled up past messages they have not seen.
  const [hasNewBelow, setHasNewBelow] = useState(false);
  // A block exists in one direction or the other. Which way round is
  // deliberately not shown: telling someone they have been blocked is not this
  // app's to disclose, and the practical answer is the same either way.
  const [messagingUnavailable, setMessagingUnavailable] = useState(false);
  // Distinguishes the first render of a thread from later updates.
  const hasRenderedMessages = useRef(false);

  const { typingUsers, sendTypingIndicator } = useRealtimeMessaging(conversation.id, {
    // Names for the indicator come from the real participants, so it can never
    // again announce a person who is not in the conversation.
    participants: conversation.participants.map((p) => ({ id: p.id, name: p.name })),
    // A broadcast carries no content, so refetch through the gated endpoint —
    // that is what keeps a locked conversation locked. Silent, because the
    // reader is already looking at this thread.
    onNewMessage: () => {
      loadMessages({ silent: true });
      // Arriving while the thread is open counts as read. Without this the
      // message appears in front of the reader and still adds to their unread
      // badge, because the mark-on-open effect only runs when the conversation
      // changes.
      if (!conversation.contentLocked) {
        messagingService.markConversationAsRead(conversation.id);
      }
    },
  });

  /**
   * Fetch the thread.
   *
   * `silent` is the difference between opening a conversation and refreshing
   * one you are already reading. isLoading swaps the ENTIRE pane for "Loading
   * messages..." — header, thread and composer — so a background refresh tore
   * the whole chat down and rebuilt it every time a message arrived. It also
   * unmounted the composer, which threw away anything half-typed.
   *
   * A silent refresh leaves the UI in place and swaps the message list under
   * it; React reconciles by message id, so existing bubbles are not remounted.
   */
  const loadMessages = async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }
      const fetchedMessages = await messagingService.getMessages(conversation.id);
      setMessages(fetchedMessages);
    } catch (err) {
      // A failed background refresh must not replace a readable thread with an
      // error screen — what is already on screen is still valid.
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      } else {
        logger.error('Background message refresh failed:', err);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [conversation.id]);

  // Opening a thread reads it. Nothing did this before — the per-message
  // endpoint existed and had no caller — so once unread counts became real
  // they would have stuck at their high-water mark forever.
  //
  // The server declines to mark a LOCKED conversation read: seeing that
  // someone wrote is not reading what they wrote, and clearing that badge
  // would remove the only prompt to come back.
  useEffect(() => {
    if (!conversation.id) return;
    if (conversation.contentLocked) return;
    messagingService.markConversationAsRead(conversation.id);
  }, [conversation.id, conversation.contentLocked]);

  /**
   * Auto-scroll, but only when the reader is already at the bottom.
   *
   * This used to scroll on every change to `messages`, so a message arriving
   * while someone was reading back through the conversation yanked them to the
   * newest one and lost their place.
   *
   * Three cases, and only the first two should move the view:
   *   - the thread just opened            → jump to the newest, no animation
   *   - the reader is already at the end  → follow the conversation
   *   - the reader has scrolled up        → leave them alone, and tell them
   *                                         something arrived
   */
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Within this many pixels of the end still counts as "at the bottom" —
  // exact equality never holds with fractional scroll heights and zoom.
  const BOTTOM_THRESHOLD_PX = 80;

  const isNearBottom = () => {
    const list = messageListRef.current;
    if (!list) return true;
    return list.scrollHeight - list.scrollTop - list.clientHeight <= BOTTOM_THRESHOLD_PX;
  };

  const handleListScroll = () => {
    if (isNearBottom()) setHasNewBelow(false);
  };

  useEffect(() => {
    if (messages.length === 0) return;

    // First paint of a thread lands at the bottom without animating — the
    // conversation should open where it left off, not scroll there.
    if (!hasRenderedMessages.current) {
      hasRenderedMessages.current = true;
      scrollToBottom('auto');
      return;
    }

    const last = messages[messages.length - 1];
    const isOwn = last?.senderId === currentUserId;

    // Your own message always scrolls: you just pressed send, you expect to
    // see it.
    if (isOwn || isNearBottom()) {
      scrollToBottom();
      setHasNewBelow(false);
    } else {
      setHasNewBelow(true);
    }
  }, [messages, currentUserId]);

  // A new thread starts its own scroll history.
  useEffect(() => {
    hasRenderedMessages.current = false;
    setHasNewBelow(false);
    setMessagingUnavailable(false);
  }, [conversation.id]);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };


  const handleTyping = (isTyping: boolean) => {
    // The id travels with the event: the receiver needs to know who is typing,
    // and it is also how a client ignores an echo of itself.
    sendTypingIndicator(isTyping, currentUserId);
  };

  const handleSendMessage = async (content: string, attachments?: any[]) => {
    try {
      await onSendMessage(content, attachments);
      // Silent: the sender is looking straight at this thread. A non-silent
      // reload flips isLoading, which swaps the entire pane — header, thread
      // and composer — for "Loading messages...", so pressing send tore down
      // the conversation you were having. The receiving side was already
      // silent; this was the same bug on the other half of the exchange.
      await loadMessages({ silent: true });
    } catch (error: any) {
      // Shown, not just logged. A send rejected by the paywall used to make
      // the message disappear with no explanation at all.
      console.error('Failed to send message:', error);
      // "You cannot message this user" is the server's answer when a block
      // exists in EITHER direction. It is not a transient failure, so
      // "please try again" is the one thing not to say — the composer is
      // replaced with an explanation instead.
      const message = typeof error?.message === 'string' ? error.message : '';
      if (message.toLowerCase().includes('cannot message this user')) {
        setMessagingUnavailable(true);
        setSendError(null);
        return;
      }

      setSendError(error?.code === 'PREMIUM_REQUIRED'
        ? (error.message || 'Upgrade to Premium to reply outside of Message Bets')
        : 'Your message could not be sent. Please try again.');
    }
  };

  // Clear a paywall error once the plan changes: an upgrade should not leave
  // "Upgrade to Premium to reply" sitting above a composer that now works.
  useEffect(() => {
    if (isPremium) setSendError(null);
  }, [isPremium]);

  const handleDeleteMessage = async (messageId: string) => {
    try {
      // Remove message from local state immediately for better UX
      setMessages(prev => prev.filter(msg => msg.id !== messageId));

      // Call the messaging service to delete the message
      await messagingService.deleteMessage(messageId);

      logger.log('Message deleted successfully:', messageId);
    } catch (error) {
      console.error('Failed to delete message:', error);
      // Reload messages to restore the message if deletion failed. Silent, like every other refresh of a
      // thread already on screen — the reader is watching it.
      await loadMessages({ silent: true });
    }
  };

  /**
   * Remove a message from this viewer's thread only.
   *
   * Optimistic, like delete: the row is already gone from the server's answer
   * for this user, so the only thing to restore on failure is the local list.
   */
  const handleHideMessage = async (messageId: string) => {
    const previous = messages;
    try {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      await messagingService.hideMessage(messageId);
    } catch (error) {
      console.error('Failed to hide message:', error);
      setMessages(previous);
      setSendError('That message could not be removed. Please try again.');
    }
  };

  const handleEditMessage = async (messageId: string, newContent: string) => {
    try {
      // Update message in local state immediately
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, content: newContent, isEdited: true, editedAt: new Date() }
          : msg
      ));

      // Call the messaging service to update the message
      await messagingService.editMessage(messageId, newContent);

      logger.log('Message edited successfully:', messageId);
    } catch (error: any) {
      console.error('Failed to edit message:', error);
      // Say why. The five-minute window is enforced server-side, so an edit
      // attempted after it closes comes back as a real rejection rather than a
      // generic failure, and the user deserves that answer.
      setSendError(
        typeof error?.message === 'string' && error.message.includes('EDIT_WINDOW_EXPIRED')
          ? 'Messages can only be edited for 5 minutes after sending.'
          : 'That edit could not be saved. Please try again.',
      );
      // Restore the original content. Silent, like every other refresh of a
      // thread already on screen — the reader is watching it.
      await loadMessages({ silent: true });
    }
  };

  const otherParticipant = conversation.participants.find(p => p.id !== currentUserId);

  // See canReplyInConversation for why this combines three inputs rather than
  // trusting the fetched flag alone.
  const canReply = canReplyInConversation(conversation, isPremium);

  if (isLoading) {
    return (
      <ChatContainer>
        <ChatLoading>Loading messages...</ChatLoading>
      </ChatContainer>
    );
  }

  if (error) {
    return (
      <ChatContainer>
        <ChatError>
          <div>Error loading messages</div>
          <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</div>
        </ChatError>
      </ChatContainer>
    );
  }

  return (
    <ChatContainer>
      <ChatHeader>
        <ChatHeaderInfo>
          <ChatHeaderAvatar>
            {otherParticipant?.avatar ? (
              <img
                src={otherParticipant.avatar}
                alt={otherParticipant.name}
                style={{ width: '100%', height: '100%', borderRadius: '50%' }}
              />
            ) : (
              getInitials(otherParticipant?.name || 'U')
            )}
          </ChatHeaderAvatar>

          <ChatHeaderDetails>
            <ChatHeaderName>{otherParticipant?.name || 'Unknown User'}</ChatHeaderName>

          </ChatHeaderDetails>
        </ChatHeaderInfo>

        <ChatHeaderActions>

          <ChatActionButton
            title="Conversation Settings"
            onClick={() => setShowSettings(true)}
          >
            ⚙️
          </ChatActionButton>
        </ChatHeaderActions>
      </ChatHeader>

      <MessageList ref={messageListRef} onScroll={handleListScroll}>
        {conversation.contentLocked && messages.length > 0 ? (
          // CONTENT LOCKED. Outside a Message Bet, a free member is told that
          // someone wrote and how many times, but not what was said — the text
          // is not in the payload to render, the server withheld it.
          //
          // The count is deliberate: "3 messages waiting" is a far stronger
          // reason to subscribe than an unexplained blank thread, and it is
          // true without revealing anything.
          <EmptyChatState>
            <EmptyChatIcon>🔒</EmptyChatIcon>
            <EmptyChatText>
              {messages.length === 1
                ? `${otherParticipant?.name || 'This member'} sent you a message.`
                : `${otherParticipant?.name || 'This member'} sent you ${messages.length} messages.`}
              <br />
              {conversation.contentLockedReason
                || 'Upgrade to Premium to read messages sent outside of Message Bets.'}
            </EmptyChatText>
            <ReplyLockButton type="button" onClick={() => setShowPremiumModal(true)}>
              Upgrade to read
            </ReplyLockButton>
          </EmptyChatState>
        ) : messages.length === 0 ? (
          <EmptyChatState>
            <EmptyChatIcon>💬</EmptyChatIcon>
            <EmptyChatText>No messages yet. Start the conversation!</EmptyChatText>
          </EmptyChatState>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === currentUserId;

            return (
              <MessageItem
                key={message.id}
                message={message}
                isOwn={isOwn}
                conversations={conversations}
                onAction={async (messageId, action, data) => {
                  // Handle different actions
                  switch (action) {
                    case 'delete':
                    case 'unsend':
                      // Both remove the message for everyone.
                      await handleDeleteMessage(messageId);
                      break;
                    case 'hide':
                      await handleHideMessage(messageId);
                      break;
                    case 'edit':
                      if (data?.newContent) {
                        await handleEditMessage(messageId, data.newContent);
                      }
                      break;
                    case 'reply':
                      // Reply to message logic
                      logger.log('Reply to message:', messageId, data);
                      break;
                    case 'forward':
                      // Forward message logic
                      logger.log('Forward message:', messageId, data);
                      break;
                    default:
                      onMessageAction(messageId, action);
                  }
                }}
              />
            );
          })
        )}

        {typingUsers.length > 0 && (
          <TypingIndicator users={typingUsers} />
        )}

        <div ref={messagesEndRef} />
      </MessageList>

      {/* Not scrolling the reader means they could miss what arrived, so say
          so instead. Without this, "don't yank" quietly becomes "don't tell". */}
      {hasNewBelow && (
        <NewMessagesPill
          type="button"
          onClick={() => { scrollToBottom(); setHasNewBelow(false); }}
        >
          ↓ New messages
        </NewMessagesPill>
      )}

      {messagingUnavailable && !conversation.isBlocked ? (
        // Blocked, but not by this viewer — so there is nothing for them to
        // undo and nothing to explain beyond the fact that it is not possible.
        <ReplyLockPanel>
          <ReplyLockTitle>
            You can't message {otherParticipant?.name || 'this member'}.
          </ReplyLockTitle>
          <ReplyLockText>
            Messages aren't going through between you two. Everything already in
            this conversation stays here.
          </ReplyLockText>
        </ReplyLockPanel>
      ) : conversation.isBlocked ? (
        // BLOCKED BY THIS VIEWER. can_users_message refuses in BOTH
        // directions, so a composer here accepted a message, sent it, and came
        // back with "Your message could not be sent. Please try again." —
        // advice that could never work, on a block the user had set themselves
        // and was given no way to see or undo from this screen.
        <ReplyLockPanel>
          <ReplyLockTitle>
            You blocked {otherParticipant?.name || 'this member'}.
          </ReplyLockTitle>
          <ReplyLockText>
            Neither of you can message the other while the block is in place.
            Unblock them to start talking again.
          </ReplyLockText>
          <ReplyLockButton
            type="button"
            onClick={async () => {
              try {
                await messagingService.unblockUser(conversation.id, otherParticipant?.id || '');
                onConversationChanged?.();
              } catch (error) {
                console.error('Failed to unblock:', error);
                setSendError('That unblock could not be saved. Please try again.');
              }
            }}
          >
            Unblock {otherParticipant?.name || 'this member'}
          </ReplyLockButton>
        </ReplyLockPanel>
      ) : canReply ? (
        <>
          {sendError && <ReplyLockPanel role="alert">{sendError}</ReplyLockPanel>}
          <MessageComposer
            onSendMessage={handleSendMessage}
            onTyping={handleTyping}
            placeholder={`Message ${otherParticipant?.name || 'user'}...`}
          />
        </>
      ) : (
        // Reply lock. In a bet the content is visible and only sending is
        // held back; outside one the thread above is locked as well, and this
        // panel carries the same upgrade offer either way.
        <ReplyLockPanel>
          <ReplyLockTitle>
            {otherParticipant?.isPremium
              ? `${otherParticipant?.name || 'This member'} is a Premium member and messaged you directly.`
              : `${otherParticipant?.name || 'This member'} messaged you directly.`}
          </ReplyLockTitle>
          <ReplyLockText>
            {conversation.replyLockedReason
              || 'Replying outside of Message Bets is a Premium feature. Message Bets stay free.'}
          </ReplyLockText>
          <ReplyLockButton type="button" onClick={() => setShowPremiumModal(true)}>
            Upgrade to reply
          </ReplyLockButton>
        </ReplyLockPanel>
      )}

      <PremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />

      {/* Conversation Settings Modal */}
      {showSettings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <ConversationSettings
            conversation={conversation}
            currentUserId={currentUserId}
            onMuteConversation={async (conversationId) => {
              await messagingService.muteConversation(conversationId);
              logger.log('Mute conversation:', conversationId);
              onConversationChanged?.();
              // In a real app, we would reload the conversation here
            }}
            onUnmuteConversation={async (conversationId) => {
              await messagingService.unmuteConversation(conversationId);
              logger.log('Unmute conversation:', conversationId);
            }}
            onBlockUser={async (conversationId, userId) => {
              await messagingService.blockUser(conversationId, userId);
              logger.log('Block user:', userId, 'in conversation:', conversationId);
              setShowSettings(false);
              // The list still shows this conversation until something reloads
              // it — blocking someone and watching their thread sit there is
              // not a convincing block.
              onConversationChanged?.();
            }}
            onUnblockUser={async (conversationId, userId) => {
              // Was a console.log and nothing else, so a block made here could
              // never be undone here.
              await messagingService.unblockUser(conversationId, userId);
              logger.log('Unblock user:', userId, 'in conversation:', conversationId);
              setShowSettings(false);
              onConversationChanged?.();
            }}
            onLeaveConversation={async (conversationId) => {
              logger.log('Leave conversation:', conversationId);
              setShowSettings(false);
            }}
            onDeleteConversation={async (conversationId) => {
              await messagingService.deleteConversation(conversationId);
              logger.log('Delete conversation:', conversationId);
              setShowSettings(false);
              onConversationChanged?.();
            }}
            onClose={() => setShowSettings(false)}
          />
        </div>
      )}
    </ChatContainer>
  );
};
